import { useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import {
  Dropdown,
  DropdownContent,
  DropdownDivider,
  IconButton,
} from 'ui-components';
import { MoreIcon } from 'ui-components/dist/MoreIcon';
import { Feature } from '~/components/Feature';
import { ManagerDropdown } from '~/components/ManagerDropdown';
import { FEATURE_FLAGS } from '~/constants';
import { useGetSavedLists } from '~/hooks/api/useSavedLists';
import { useTalentManagerAccess } from '~/hooks/useTalentManagerAccess';
import { AddingToAnExistingListModal } from '~/screens/MyAgency/components/SavedListButton/AddingToAnExistingListModal';
import { useSavedListsStore } from '~/screens/MyAgency/store';
import { selectTalentManager } from '~/selectors/talent-manager';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';

import { SavedListCreateListModal } from '../SavedListCreateListModal';
import { AddNoteDropdownItem } from './AddNoteDropdownItem';
import { AddToListDropdownItem } from './AddToListDropdownItem';
import { EditInfoDropdownItem } from './EditInfoDropdownItem';
import { EditPlatformsDropdownItem } from './EditPlatformsDropdownItem';
import { ITalentDropdownMenu } from './TalentDropdownMenu.types';
import { WebLinksDropdownItem } from './WebLinksDropdownItem';

export const CreatorProfileDropdownMenu: React.FC<ITalentDropdownMenu> = ({
  creator,
  isShared,
  note,
  onNoteOpen,
}) => {
  const { clearStore } = useSavedListsStore();
  const { data } = useGetSavedLists({ saved: true });

  const {
    isOpen: isAddCreatorToListModalOpen,
    onClose: onAddCreatorToListModalClose,
    onOpen: onAddCreatorToListModalOpen,
  } = useDisclosure();

  const manager = useSelector(selectTalentManager);

  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });

  // used for Edit info, Web Links, Edit platforms and Add/Edit Notes dropdown menu items
  const { shouldHaveAccessAsManagerComanagerAdmin } = useTalentManagerAccess(
    creator,
    userAccessData?.data as MeResponse,
  );

  const hasSavedLists = Boolean(data?.results?.length);

  return (
    <>
      {hasSavedLists ? (
        <AddingToAnExistingListModal
          isOpen={isAddCreatorToListModalOpen}
          onClose={onAddCreatorToListModalClose}
          creator={creator}
        />
      ) : (
        <SavedListCreateListModal
          creatorIds={[creator.id]}
          isOpen={isAddCreatorToListModalOpen}
          onClose={onAddCreatorToListModalClose}
          onSuccess={() => clearStore()}
        />
      )}
      <Dropdown>
        {({ isOpen, buttonRef, getReferenceProps }) => (
          <>
            <IconButton
              aria-label="Open creator invitation actions"
              icon={<MoreIcon boxSize={4} />}
              isActive={isOpen}
              ref={buttonRef}
              size="md"
              variant="transparent"
              {...getReferenceProps()}
            />

            <DropdownContent
              __css={{
                // Required to fix my agency header z-index issue
                zIndex: 1099,
              }}
            >
              <AddToListDropdownItem onClick={onAddCreatorToListModalOpen} />

              {creator.manager && (
                <ManagerDropdown
                  manager={creator.manager}
                  title="Talent Manager"
                  isShared={isShared as boolean}
                />
              )}

              {shouldHaveAccessAsManagerComanagerAdmin && (
                <>
                  <DropdownDivider />
                  <EditInfoDropdownItem creator={creator} />
                  <Feature name={FEATURE_FLAGS.foamWebLinks}>
                    <WebLinksDropdownItem creator={creator} />
                  </Feature>
                  <Feature name={FEATURE_FLAGS.foamOffPlatformAddSocials}>
                    <EditPlatformsDropdownItem creator={creator} />
                  </Feature>
                  {/* @TODO: confirm usage of this AddNote item in Creator Profile and then, delete */}
                  <AddNoteDropdownItem
                    onNoteOpen={onNoteOpen}
                    note={note}
                    isShared={isShared}
                  />
                </>
              )}
            </DropdownContent>
          </>
        )}
      </Dropdown>
    </>
  );
};
